import { useState, useEffect } from 'react';
import api from '../services/api';
import { downloadBlob } from '../services/api';
import MonthPicker from '../components/MonthPicker';
import { useCutoffDay, addMonths, monthLabel, monthStart, showDate, todayIso } from '../utils/months';

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  h1: { fontSize: 22, fontWeight: 600 },
  btn: { padding: '8px 16px', backgroundColor: '#4a9eff', color: '#fff', border: 'none', borderRadius: 4, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnSmall: { padding: '4px 10px', backgroundColor: '#4a9eff', color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer', marginRight: 6 },
  btnDisabled: { padding: '4px 10px', backgroundColor: '#ccc', color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, cursor: 'default', marginRight: 6 },
  filterRow: { display: 'flex', gap: 12, marginBottom: 16 },
  select: { padding: '8px 10px', border: '1px solid #ddd', borderRadius: 4, fontSize: 14 },
  table: { width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  th: { textAlign: 'left', padding: '10px 14px', backgroundColor: '#f8f9fa', fontSize: 12, fontWeight: 600, color: '#666', textTransform: 'uppercase', borderBottom: '1px solid #eee' },
  td: { padding: '10px 14px', borderBottom: '1px solid #f0f0f0', fontSize: 14 },
  badge: (done) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 500,
    backgroundColor: done ? '#e6f9e6' : '#fff8e6', color: done ? '#27ae60' : '#f39c12',
  }),
  modal: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modalCard: { backgroundColor: '#fff', borderRadius: 8, padding: 24, width: 420, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
  modalTitle: { fontSize: 18, fontWeight: 600, marginBottom: 16 },
  label: { display: 'block', fontSize: 13, fontWeight: 500, color: '#555', marginBottom: 4 },
  input: { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 4, fontSize: 14, marginBottom: 12 },
  btnRow: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 },
  btnLight: { padding: '4px 10px', backgroundColor: '#fff', color: '#333', border: '1px solid #ccc', borderRadius: 4, fontSize: 12, cursor: 'pointer', marginRight: 6 },
  btnRemove: { padding: '4px 10px', backgroundColor: '#fff', color: '#e74c3c', border: '1px solid #e74c3c', borderRadius: 4, fontSize: 12, cursor: 'pointer' },
  btnCancel: { padding: '8px 16px', backgroundColor: '#f0f0f0', border: 'none', borderRadius: 4, fontSize: 13, cursor: 'pointer' },
};

export default function Editions() {
  const [titles, setTitles] = useState([]);
  const [editions, setEditions] = useState([]);
  const [selectedTitle, setSelectedTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ title_id: '', edition_date: new Date().toISOString().split('T')[0], edition_name: '' });
  const [generating, setGenerating] = useState(null);
  const [generatingCSV, setGeneratingCSV] = useState(null);
  const [anpostEnabled, setAnpostEnabled] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  // the edition being renamed ({ id, edition_name, edition_date })
  const [editEd, setEditEd] = useState(null);
  const [editError, setEditError] = useState('');
  // sold by the month (Settings): an edition is a month
  const cutoff = useCutoffDay();
  const newDate = () => (cutoff ? addMonths(todayIso(), 1) : todayIso());
  const reload = () => api.getEditions({ title_id: selectedTitle }).then(setEditions);

  useEffect(() => {
    api.getTitles().then(t => {
      setTitles(t);
      if (t.length > 0) setSelectedTitle(t[0].id.toString());
    });
    api.getSettings().then(s => {
      setAnpostEnabled(s.anpost_enabled === 'true');
    });
  }, []);

  useEffect(() => {
    if (!selectedTitle) return;
    setLoading(true);
    api.getEditions({ title_id: selectedTitle }).then(setEditions).finally(() => setLoading(false));
  }, [selectedTitle]);

  const handleCreate = async () => {
    if (!form.title_id || !form.edition_date) return;
    await api.createEdition(form);
    setShowModal(false);
    if (form.title_id === selectedTitle) reload();
  };

  const openNew = () => {
    setForm({ title_id: selectedTitle, edition_date: newDate(), edition_name: '' });
    setShowModal(true);
  };

  const openEdit = (e) => {
    setEditError('');
    setEditEd({ id: e.id, edition_name: e.edition_name, edition_date: String(e.edition_date).slice(0, 10) });
  };

  const handleSaveEdit = async () => {
    if (!editEd.edition_name.trim()) { setEditError('The edition needs a name'); return; }
    if (!editEd.edition_date) { setEditError('The edition needs a date'); return; }
    try {
      await api.updateEdition(editEd.id, { edition_name: editEd.edition_name.trim(), edition_date: editEd.edition_date });
      setEditEd(null);
      reload();
    } catch (err) {
      setEditError(err.message || 'Could not save the edition');
    }
  };

  const handleRemove = async (e) => {
    const made = e.labels_generated ? `\n\nLabels were made for it${e.labels_generated_at ? ' on ' + new Date(e.labels_generated_at).toLocaleDateString('en-GB') : ''}.` : '';
    if (!window.confirm(`Remove the edition "${e.edition_name}"?${made}\n\nSubscribers and their subscriptions are not changed.`)) return;
    try {
      await api.deleteEdition(e.id);
      reload();
    } catch (err) {
      alert(err.message || 'Could not remove the edition');
    }
  };

  const handleGenerateLabels = async (editionId) => {
    setGenerating(editionId);
    try {
      const countData = await api.getLabelCount(editionId);
      if (countData.count === 0) {
        alert(countData.reason || (anpostEnabled ? 'No domestic (Ireland) subscribers for this edition' : 'No active subscribers for this edition'));
        return;
      }
      const labelType = anpostEnabled ? 'domestic ' : '';
      if (!window.confirm(`Generate ${labelType}labels for ${countData.count} subscriber${countData.count !== 1 ? 's' : ''}?`)) return;

      const result = await api.generateLabels(editionId);
      if (result.blob) {
        const edition = editions.find(e => e.id === editionId);
        const filename = `labels-${(edition?.edition_name || 'edition').replace(/[^a-zA-Z0-9]/g, '-')}.pdf`;
        downloadBlob(result.blob, filename);
        api.getEditions({ title_id: selectedTitle }).then(setEditions);
      }
    } catch (err) {
      alert(err.message || 'Failed to generate labels');
    } finally {
      setGenerating(null);
    }
  };

  const handleAnPostCSV = async (editionId) => {
    setGeneratingCSV(editionId);
    try {
      const countData = await api.getAnPostCount(editionId);
      if (countData.count === 0) {
        alert(countData.reason || 'No international subscribers for this edition');
        return;
      }
      if (!window.confirm(`Generate An Post CSV for ${countData.count} international subscriber${countData.count !== 1 ? 's' : ''}?`)) return;

      const result = await api.generateAnPostCSV(editionId);
      if (result.blob) {
        const edition = editions.find(e => e.id === editionId);
        const filename = `anpost-${(edition?.edition_name || 'edition').replace(/[^a-zA-Z0-9]/g, '-')}.csv`;
        downloadBlob(result.blob, filename);
      }
    } catch (err) {
      alert(err.message || 'Failed to generate An Post CSV');
    } finally {
      setGeneratingCSV(null);
    }
  };

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.h1}>Editions</h1>
        <button style={styles.btn} onClick={openNew}>+ New Edition</button>
      </div>

      <div style={styles.filterRow}>
        <select style={styles.select} value={selectedTitle} onChange={e => setSelectedTitle(e.target.value)}>
          {titles.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <button
          onClick={() => setShowHelp(!showHelp)}
          style={{ padding: '8px 12px', background: 'none', border: '1px solid #ddd', borderRadius: 4, fontSize: 13, color: '#4a9eff', cursor: 'pointer', fontWeight: 500 }}
        >{showHelp ? 'Hide help' : 'How does this work?'}</button>
      </div>

      {showHelp && (
        <div style={{ backgroundColor: '#f8faff', border: '1px solid #d6e4f0', borderRadius: 8, padding: '16px 20px', marginBottom: 16, fontSize: 13, lineHeight: 1.7, color: '#444' }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10, color: '#222' }}>Generating Mailing Labels & An Post CSV</div>

          <div style={{ fontWeight: 600, marginBottom: 4 }}>Step 1: Create an edition</div>
          <p style={{ margin: '0 0 10px' }}>Click <b>+ New Edition</b>, select the title and date. An edition represents a single issue of your publication.</p>

          {anpostEnabled ? (
            <>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Step 2: Generate domestic labels</div>
              <p style={{ margin: '0 0 10px' }}>Click <b>Domestic Labels</b> to download a PDF of mailing labels for all Irish subscribers with an active subscription to this title. Print these on your Avery label sheets as normal.</p>

              <div style={{ fontWeight: 600, marginBottom: 4 }}>Step 3: Generate An Post CSV</div>
              <p style={{ margin: '0 0 10px' }}>Click the green <b>An Post CSV</b> button to download a CSV file for all international subscribers. This file is pre-formatted with postage rates, HS codes, and customs details — ready to upload to An Post's system for international dispatch.</p>

              <div style={{ fontWeight: 600, marginBottom: 4 }}>Important: subscriber countries</div>
              <p style={{ margin: '0 0 10px' }}>The system splits subscribers by the <b>Country</b> field. Irish subscribers should have their country set to <b>"Ireland"</b>. International subscribers need their actual country (e.g. "United Kingdom", "United States"). You can check and update countries on each subscriber's detail page.</p>

              <div style={{ background: '#eef6ee', borderRadius: 4, padding: '8px 12px', fontSize: 12, color: '#555' }}>
                <b>Tip:</b> An Post settings (account number, postage rates, HS codes) can be configured in <b>Settings</b> under "An Post International".
                To generate labels for all subscribers regardless of country, disable An Post in Settings.
              </div>
            </>
          ) : (
            <>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Step 2: Generate labels</div>
              <p style={{ margin: '0 0 10px' }}>Click <b>Generate Labels</b> to download a PDF of mailing labels for all subscribers with an active subscription to this title. Print these on your Avery label sheets.</p>

              <div style={{ background: '#fff8e6', borderRadius: 4, padding: '8px 12px', fontSize: 12, color: '#555' }}>
                <b>Tip:</b> If you post internationally, enable <b>An Post International</b> in Settings to split domestic and international subscribers automatically and generate An Post-formatted CSV files for international dispatch.
              </div>
            </>
          )}
        </div>
      )}

      {loading ? <div>Loading...</div> : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Edition</th>
              <th style={styles.th}>{cutoff ? 'Month' : 'Date'}</th>
              <th style={styles.th}>Labels</th>
              <th style={styles.th}>Subscribers due</th>
              <th style={styles.th}></th>
              <th style={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {editions.map(e => (
              <tr key={e.id}>
                <td style={styles.td}>{e.edition_name}</td>
                <td style={styles.td}>{showDate(e.edition_date, cutoff)}</td>
                <td style={styles.td}>
                  <span style={styles.badge(e.labels_generated)}>
                    {e.labels_generated ? 'Generated' : 'Pending'}
                  </span>
                </td>
                <td style={styles.td} data-testid={`edition-due-${e.id}`}>
                  {e.due_count > 0 ? (
                    <>
                      {e.due_count}
                      {anpostEnabled && (
                        <div style={{ fontSize: 12, color: '#888' }}>{e.due_domestic} Ireland · {e.due_international} international</div>
                      )}
                    </>
                  ) : (
                    <span style={{ color: '#999' }}>None due</span>
                  )}
                </td>
                <td style={styles.td}>
                  <button
                    style={generating === e.id ? styles.btnDisabled : styles.btnSmall}
                    onClick={() => handleGenerateLabels(e.id)}
                    disabled={generating === e.id}
                  >
                    {generating === e.id ? 'Generating...' : anpostEnabled ? 'Domestic Labels' : (e.labels_generated ? 'Re-generate' : 'Generate Labels')}
                  </button>
                  {anpostEnabled && (
                    <button
                      style={generatingCSV === e.id ? styles.btnDisabled : { ...styles.btnSmall, backgroundColor: '#27ae60' }}
                      onClick={() => handleAnPostCSV(e.id)}
                      disabled={generatingCSV === e.id}
                    >
                      {generatingCSV === e.id ? 'Generating...' : 'An Post CSV'}
                    </button>
                  )}
                </td>
                <td style={{ ...styles.td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                  <button style={styles.btnLight} onClick={() => openEdit(e)}>Rename</button>
                  <button style={styles.btnRemove} onClick={() => handleRemove(e)}>Remove</button>
                </td>
              </tr>
            ))}
            {editions.length === 0 && (
              <tr><td style={styles.td} colSpan={6}>No editions yet. Create one to get started.</td></tr>
            )}
          </tbody>
        </table>
      )}

      {editEd && (
        <div style={styles.modal} onClick={() => setEditEd(null)}>
          <div style={styles.modalCard} onClick={e => e.stopPropagation()}>
            <div style={styles.modalTitle}>Rename edition</div>
            {editError && <div style={{ color: '#c0392b', marginBottom: 10, fontSize: 13 }}>{editError}</div>}
            <label style={styles.label}>Edition Name</label>
            <input style={styles.input} value={editEd.edition_name} onChange={e => setEditEd({ ...editEd, edition_name: e.target.value })} autoFocus data-testid="edit-edition-name" />
            <label style={styles.label}>{cutoff ? 'Edition Month' : 'Edition Date'}</label>
            {cutoff ? (
              <div style={{ marginBottom: 12 }}>
                <MonthPicker style={styles.input} value={monthStart(editEd.edition_date)} onChange={v => setEditEd({ ...editEd, edition_date: v })} testId="edit-edition-month" />
              </div>
            ) : (
              <input style={styles.input} type="date" value={editEd.edition_date} onChange={e => setEditEd({ ...editEd, edition_date: e.target.value })} />
            )}
            <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
              The edition goes to everyone whose subscription covers its {cutoff ? 'month' : 'date'}.
            </div>
            <div style={styles.btnRow}>
              <button style={styles.btnCancel} onClick={() => setEditEd(null)}>Cancel</button>
              <button style={styles.btn} onClick={handleSaveEdit}>Save</button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div style={styles.modal} onClick={() => setShowModal(false)}>
          <div style={styles.modalCard} onClick={e => e.stopPropagation()}>
            <div style={styles.modalTitle}>New Edition</div>
            <label style={styles.label}>Title</label>
            <select style={{ ...styles.input }} value={form.title_id} onChange={e => setForm({ ...form, title_id: e.target.value })}>
              <option value="">Select a title...</option>
              {titles.filter(t => t.active).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <label style={styles.label}>{cutoff ? 'Edition Month' : 'Edition Date'}</label>
            {cutoff ? (
              <div style={{ marginBottom: 12 }}>
                <MonthPicker style={styles.input} value={monthStart(form.edition_date)} onChange={v => setForm({ ...form, edition_date: v })} testId="new-edition-month" />
              </div>
            ) : (
              <input style={styles.input} type="date" value={form.edition_date} onChange={e => setForm({ ...form, edition_date: e.target.value })} />
            )}
            <label style={styles.label}>Edition Name (auto-generated if blank)</label>
            <input style={styles.input} value={form.edition_name} onChange={e => setForm({ ...form, edition_name: e.target.value })}
              placeholder={cutoff ? `e.g. ${monthLabel(form.edition_date || todayIso())}` : 'e.g. Issue 234 - 14 Feb 2026'} />
            <div style={styles.btnRow}>
              <button style={styles.btnCancel} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={styles.btn} onClick={handleCreate}>Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
